import { useState } from "react";
import {
  DragStartEvent,
  DragEndEvent,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import { Section, Field, DragEventData } from "../components/FormLayout/types";
import {
  isAvailableField,
  isCustomField,
  isSectionField,
} from "../components/FormLayout/utils";

interface UseFormDnDProps {
  sections: Section[];
  availableFieldsState: {
    systemFields: Record<string, Field>;
    customFields: Record<string, Field>;
  };
  setSections: React.Dispatch<React.SetStateAction<Section[]>>;
  setAvailableFieldsState: React.Dispatch<
    React.SetStateAction<{
      systemFields: Record<string, Field>;
      customFields: Record<string, Field>;
    }>
  >;
  findAvailableFields: (key: string) => Field | undefined;
}

export function useFormDnD({
  sections,
  setSections,
  setAvailableFieldsState,
  findAvailableFields,
}: UseFormDnDProps) {
  const [draggedField, setDraggedField] = useState<Field | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeId = String(active.id);

    // Handle available field drag
    if (isAvailableField(activeId)) {
      const field = active.data?.current;
      if (field?.id) {
        const updatedField = findAvailableFields(field.id);
        updatedField && setDraggedField(updatedField);
      }
      return;
    }

    if (isSectionField(activeId)) {
      const { field, sectionId } = active.data.current as {
        field: Field & { name?: string; label?: string };
        sectionId: string;
      };

      if (!field?.label || !sectionId) {
        setDraggedField(null);
        return;
      }

      if (field.label) {
        const updatedField = findAvailableFields(field.id);
        setDraggedField({ ...(updatedField || field), section_id: sectionId });
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const active = event.active as unknown as DragEventData;
    const destination = event.over as unknown as DragEventData;

    if (!destination) {
      setDraggedField(null);
      return;
    }

    if (isAvailableField(active.id)) {
      handleMoveFromAvailableToSection(active, destination);
    } else if (
      active.data?.current?.sectionId == destination.data?.current?.sectionId
    ) {
      handleSectionFieldReordering(active, destination);
    } else if (
      active.data?.current?.sectionId !== destination.data?.current?.sectionId
    ) {
      handleFieldMovementBetweenSections(active, destination);
    }
  };

  const removeFieldFromAvailableFields = (fields: any, field: Field) => {
    isCustomField(field.id)
      ? delete fields.customFields[field.id]
      : delete fields.systemFields[field.id];
    return fields;
  };

  const handleSectionFieldReordering = (
    active: DragEventData,
    over: DragEventData,
  ) => {
    const section = sections.find(
      (s) => String(s.id) === String(active.data?.current?.sectionId),
    );
    if (!section) return;
    const sourceFieldIndex = section.fields.findIndex(
      (f) => String(f.id) === active.data?.current?.field?.id,
    );
    const targetFieldIndex = section.fields.findIndex(
      (f) => String(f.id) === over.data?.current?.field?.id,
    );
    if (targetFieldIndex === -1 || sourceFieldIndex === -1) return;
    // Always insert at the target index to allow moving to the top
    const newPosition = targetFieldIndex;

    setSections((prevSections) => {
      const newFields = [...section.fields];
      const [movedField] = newFields.splice(sourceFieldIndex, 1);
      newFields.splice(newPosition, 0, movedField);
      return prevSections.map((s) => {
        return s.id === section.id ? { ...s, fields: newFields } : s;
      });
    });
  };

  const handleFieldMovementBetweenSections = (
    active: DragEventData,
    over: DragEventData,
  ) => {
    const { field, sectionId: sourceSectionId } = active.data?.current || {};
    const targetSectionId = over.data?.current?.sectionId;
    const targetFieldId = over.data?.current?.field?.id;
    if (!field?.id || !sourceSectionId || draggedField?.readOnly) return;
    const movedField = { ...field };
    setSections((prevSections) => {
      const source = prevSections.find(
        (s) => String(s.id) === String(sourceSectionId),
      );
      if (!source || source.disabled) return prevSections;
      const target = prevSections.find(
        (s) => String(s.id) === String(targetSectionId),
      );
      if ((!target && targetSectionId !== sourceSectionId) || target?.disabled)
        return prevSections;
      return prevSections.map((section) => {
        if (String(section.id) === String(sourceSectionId)) {
          return {
            ...section,
            fields: section.fields.filter((f) => f.id !== movedField.id),
          };
        }
        if (String(section.id) === String(targetSectionId)) {
          const newFields = [...section.fields];
          const targetIndex = newFields.findIndex(
            (field) => field.id === targetFieldId,
          );
          if (targetIndex !== -1) {
            newFields.splice(targetIndex, 0, movedField);
          } else {
            newFields.push(movedField);
          }
          return { ...section, fields: newFields };
        }
        return section;
      });
    });
  };

  const handleMoveFromAvailableToSection = (
    _active: DragEventData,
    over: DragEventData,
  ) => {
    if (!draggedField || draggedField?.readOnly) return;
    const targetSection = sections.find(
      (s) => String(s.id) === String(over.data?.current?.sectionId),
    );
    if (targetSection?.disabled) return;
    setAvailableFieldsState((fields) =>
      removeFieldFromAvailableFields(fields, draggedField),
    );
    setSections((sections) =>
      sections.map((section) => {
        if (String(section.id) === String(over.data?.current?.sectionId)) {
          const targetFields = [...section.fields];
          let destinationIndex = targetFields.length;
          if (isSectionField(over.id)) {
            const overFieldIndex = targetFields.findIndex(
              (f) => String(f.id) === over.data?.current?.field?.id,
            );
            if (overFieldIndex !== -1) {
              destinationIndex = overFieldIndex;
            }
          }
          targetFields.splice(destinationIndex, 0, draggedField);
          return { ...section, fields: targetFields };
        }
        return section;
      }),
    );
  };

  return {
    sensors,
    draggedField,
    setDraggedField,
    handleDragStart,
    handleDragEnd,
    handleSectionFieldReordering,
    handleFieldMovementBetweenSections,
    handleMoveFromAvailableToSection,
  };
}
